import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Typography,
  Button,
  Container,
  CircularProgress,
  Box,
  Grid,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Link as MuiLink,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import CycleSidePanel from './CycleSidePanel';
import CycleNotesModule from './CycleNotesModule';
import CycleFilesModule from './CycleFilesModule';
import { useUser } from './UserContext';
import { formatDate } from './utils/dateUtils';
import { Divider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

// --- Helper Components ---

const StatusChip = ({ status, cicloStatus }) => {
  const displayStatus = cicloStatus && cicloStatus !== 'activo' 
    ? cicloStatus.charAt(0).toUpperCase() + cicloStatus.slice(1) 
    : status;
  
  const statusStyles = {
    'Borrador': { label: 'Borrador', color: 'default' },
    'Ganador': { label: 'Ganador', color: 'success' },
    'Cancelado': { label: 'Cancelado', style: { backgroundColor: '#000000', color: 'white' } },
    'Terminado': { label: 'Terminado', style: { backgroundColor: '#FFC107', color: 'black' } },
  };

  const styleInfo = statusStyles[displayStatus] || { label: displayStatus, color: 'default' };

  return <Chip label={styleInfo.label} color={styleInfo.color} style={styleInfo.style} />;
};

const FamilyTree = ({ solicitud, ordenCompra }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, flexWrap: 'wrap' }}>
    {solicitud.requisicion_id && (
      <>
        <MuiLink component={Link} to={`/requisiciones/${solicitud.requisicion_id}`} sx={{ textDecoration: 'none' }}>
          <Paper elevation={3} sx={{ p: 1, '&:hover': { bgcolor: 'grey.200' } }}>
            <Typography variant="subtitle1">
              {solicitud.requisicion?.doc_numb || `REQ-${solicitud.requisicion_id.substring(0, 6)}`}
            </Typography>
          </Paper>
        </MuiLink>
        <ArrowForwardIosIcon sx={{ mx: 1 }} />
      </>
    )}
    <Paper elevation={3} sx={{ p: 1, bgcolor: 'primary.main', color: 'white' }}>
      <Typography variant="subtitle1">
        {solicitud.doc_numb || `RFQ-${solicitud.id.substring(0, 6)}`}
      </Typography>
    </Paper>
    {ordenCompra && (
      <>
        <ArrowForwardIosIcon sx={{ mx: 1 }} />
        <MuiLink component={Link} to={`/ordenes-compra/${ordenCompra.id}`} sx={{ textDecoration: 'none' }}>
          <Paper elevation={3} sx={{ p: 1, '&:hover': { bgcolor: 'grey.200' } }}>
            <Typography variant="subtitle1">
              {ordenCompra.doc_numb || `OC-${ordenCompra.id.substring(0, 6)}`}
            </Typography>
          </Paper>
        </MuiLink>
      </>
    )}
  </Box>
);

// --- Main Component ---

const SolicitudCotizacionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [solicitud, setSolicitud] = useState(null);
  const [ordenCompra, setOrdenCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useUser();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  const fetchSolicitud = async () => {
    try {
      const res = await axios.get(`/api/solicitudes_cotizacion/${id}`);
      setSolicitud(res.data);
      // Fetch HTML content
      const htmlRes = await axios.get(`/api/solicitudes_cotizacion/${id}/html`);
      let processedHtml = htmlRes.data || '';
      if (processedHtml) {
        // Convert relative image URLs to absolute URLs
        const apiBaseUrl = axios.defaults.baseURL || `${window.location.protocol}//${window.location.hostname}:8000`;
        processedHtml = processedHtml.replace(
          /src="\/uploads\/([^"]*)"/g,
          `src="${apiBaseUrl}/uploads/$1"`
        );
      }
      setHtmlContent(processedHtml);
    } catch (err) {
      throw new Error('No se pudo cargar la solicitud.');
    }
  };

  const fetchOrdenCompra = async () => {
    try {
      const ocRes = await axios.get(`/api/ordenes_compra/by_solicitud/${id}`);
      setOrdenCompra(ocRes.data);
    } catch (ocError) {
      if (ocError.response?.status !== 404) console.error("Error fetching OC:", ocError);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await fetchSolicitud();
        await fetchOrdenCompra();
        setError('');
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [id]);

  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) {
      return path;
    }
    return path; 
  };

  const handleMarkWinner = async () => {
    try {
      const res = await axios.post(`/api/solicitudes_cotizacion/${id}/mark_winner/`);
      setSolicitud(res.data);
    } catch (err) {
      setError('Error al marcar como ganador.');
    }
  };

  const handleCreateOC = async () => {
    try {
      const res = await axios.post(`/api/ordenes_compra/from_solicitud/${id}`);
      navigate(`/ordenes-compra/${res.data.id}`);
    } catch (err) {
      setError('Error al crear la Orden de Compra.');
    }
  };

  const handleCancel = async () => {
    try {
      await axios.put(`/api/solicitudes_cotizacion/${id}/status`, {
        status: 'cancelada',
      });
      navigate(`/requisiciones/${solicitud.requisicion_id}`);
    } catch (err) {
      setError('No se pudo cancelar la solicitud.');
      console.error(err);
    }
  };

  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (error && !solicitud) return <Typography color="error" align="center">{error}</Typography>;
  if (!solicitud) return <Typography align="center">No se encontró la solicitud.</Typography>;

  const isActionable = solicitud.status_ciclo !== 'cancelado' && solicitud.status_ciclo !== 'terminado';

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <FamilyTree solicitud={solicitud} ordenCompra={ordenCompra} />

      <Grid container spacing={3}>
        {/* Main Content (Left) */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Previsualización del Documento" />
            <CardContent>
              <iframe
                srcDoc={htmlContent}
                title={`RFQ Preview ${id}`}
                width="100%"
                height="800px"
                style={{ border: '1px solid #ccc' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar (Right) */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3} direction="column">
            {/* Summary Card */}
            <Grid item>
              <Card>
                <CardHeader title="Resumen" />
                <CardContent>
                  <Typography variant="body2" gutterBottom><strong>Proveedor:</strong> {solicitud.proveedor?.nombre_fiscal || 'N/A'}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Empresa:</strong> {solicitud.requisicion?.empresa?.nombre || 'N/A'}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Fecha:</strong> {formatDate(solicitud.created_at, true)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}><strong>Estado:</strong></Typography>
                    <StatusChip status={solicitud.estado} cicloStatus={solicitud.status_ciclo} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Actions Card */}
            <Grid item>
              <Card>
                <CardHeader title="Acciones" />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/solicitudes_cotizacion/editar/${id}`)} disabled={!isActionable}>Editar</Button>
                    {solicitud.estado !== 'Ganador' && solicitud.estado !== 'Cerrada' && <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handleMarkWinner} disabled={!isActionable}>Marcar como Ganador</Button>}
                    {solicitud.estado === 'Ganador' && !ordenCompra && <Button variant="contained" onClick={handleCreateOC} disabled={!isActionable}>Crear Orden de Compra</Button>}
                    {ordenCompra && <Button variant="contained" color="info" onClick={() => navigate(`/ordenes-compra/${ordenCompra.id}`)}>Ver Orden de Compra</Button>}
                    {isActionable && <Button variant="contained" color="error" onClick={handleOpenCancelDialog}>Cancelar</Button>}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
      >
        <DialogTitle>Cancelar Solicitud</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea cancelar esta solicitud? Esto cancelará también cualquier orden de compra que se haya generado a partir de aquí.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>No</Button>
          <Button onClick={() => { handleCancel(); handleCloseCancelDialog(); }} color="error" autoFocus>
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cycle Side Panel */}
      <CycleSidePanel isOpen={sidePanelOpen} onToggle={() => setSidePanelOpen(!sidePanelOpen)}>
        <CycleNotesModule elementType="solicitud_cotizacion" elementId={id} />
        <Divider sx={{ my: 3 }} />
        <CycleFilesModule elementType="solicitud_cotizacion" elementId={id} />
      </CycleSidePanel>
    </Container>
  );
};

export default SolicitudCotizacionDetail;